import { createSlice } from '@reduxjs/toolkit';
import { StudyYearCompaniesState } from '@/types/study-year-companies';
import { studyYearCompanies } from './thunks';

const initialState: StudyYearCompaniesState = {
  isLoading: false,
  error: null,
  isUpdating: false,
  isUploading: false,
  isDownloading: false,

  // Data
  companies: [],
  currentCompany: null,
  companySegments: [],
  companyContacts: [],
  companyVersions: [],
  companyHistory: [],

  // Pagination & Filters
  pagination: {
    offset: 0,
    limit: 100,
    total: undefined,
  },
  filters: {},
};

export const studyYearCompaniesSlice = createSlice({
  name: 'studyYearCompanies',
  initialState,
  reducers: {
    clearError: state => {
      state.error = null;
    },
    clearCurrentCompany: state => {
      state.currentCompany = null;
    },
    clearCompanies: state => {
      state.companies = [];
    },
    clearCompanySegments: state => {
      state.companySegments = [];
    },
    clearCompanyContacts: state => {
      state.companyContacts = [];
    },
    clearCompanyVersions: state => {
      state.companyVersions = [];
    },
    clearCompanyHistory: state => {
      state.companyHistory = [];
    },
    setFilters: (state, action) => {
      state.filters = action.payload;
    },
    setPagination: (state, action) => {
      state.pagination = { ...state.pagination, ...action.payload };
    },
  },
  extraReducers: builder => {
    builder
      // Get Study Year Companies
      .addCase(studyYearCompanies.getStudyYearCompanies.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCompanies.getStudyYearCompanies.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.companies = action.payload;
      })
      .addCase(studyYearCompanies.getStudyYearCompanies.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Study Year Company
      .addCase(studyYearCompanies.getStudyYearCompany.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCompanies.getStudyYearCompany.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        console.log(action.payload);
        state.currentCompany = action.payload;
      })
      .addCase(studyYearCompanies.getStudyYearCompany.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
        state.currentCompany = null;
      })

      // Update Study Year Company
      .addCase(studyYearCompanies.updateStudyYearCompany.pending, state => {
        state.isUpdating = true;
        state.error = null;
      })
      .addCase(studyYearCompanies.updateStudyYearCompany.fulfilled, (state, action) => {
        state.isUpdating = false;
        state.error = null;

        // Update current company if it matches
        if (
          state.currentCompany &&
          state.currentCompany.study_year_company_id === action.payload.study_year_company_id
        ) {
          state.currentCompany = { ...state.currentCompany, ...action.payload };
        }

        // Update in companies list if it exists
        const companyIndex = state.companies.findIndex(
          company => company.study_year_company_id === action.payload.study_year_company_id
        );
        if (companyIndex !== -1) {
          state.companies[companyIndex] = { ...state.companies[companyIndex], ...action.payload };
        }
      })
      .addCase(studyYearCompanies.updateStudyYearCompany.rejected, (state, action) => {
        state.isUpdating = false;
        state.error = action.payload as string;
      })

      // Get Study Year Company Segments
      .addCase(studyYearCompanies.getStudyYearCompanySegments.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCompanies.getStudyYearCompanySegments.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.companySegments = action.payload;
      })
      .addCase(studyYearCompanies.getStudyYearCompanySegments.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Download Survey
      .addCase(studyYearCompanies.downloadSurvey.pending, state => {
        state.isDownloading = true;
        state.error = null;
      })
      .addCase(studyYearCompanies.downloadSurvey.fulfilled, state => {
        state.isDownloading = false;
        state.error = null;
      })
      .addCase(studyYearCompanies.downloadSurvey.rejected, (state, action) => {
        state.isDownloading = false;
        state.error = action.payload as string;
      })

      // Upload Survey
      .addCase(studyYearCompanies.uploadSurvey.pending, state => {
        state.isUploading = true;
        state.error = null;
      })
      .addCase(studyYearCompanies.uploadSurvey.fulfilled, state => {
        state.isUploading = false;
        state.error = null;
      })
      .addCase(studyYearCompanies.uploadSurvey.rejected, (state, action) => {
        state.isUploading = false;
        state.error = action.payload as string;
      })

      // Get Study Year Company Contacts
      .addCase(studyYearCompanies.getStudyYearCompanyContacts.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCompanies.getStudyYearCompanyContacts.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.companyContacts = action.payload;
      })
      .addCase(studyYearCompanies.getStudyYearCompanyContacts.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })

      // Get Study Year Company Versions
      .addCase(studyYearCompanies.getStudyYearCompanyVersions.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCompanies.getStudyYearCompanyVersions.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.companyVersions = action.payload;
      })
      .addCase(studyYearCompanies.getStudyYearCompanyVersions.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      })
      // Get Study Year Company History
      .addCase(studyYearCompanies.getStudyYearCompanyHistory.pending, state => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(studyYearCompanies.getStudyYearCompanyHistory.fulfilled, (state, action) => {
        state.isLoading = false;
        state.error = null;
        state.companyHistory = action.payload;
      })
      .addCase(studyYearCompanies.getStudyYearCompanyHistory.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload as string;
      });
  },
});

export const {
  clearError,
  clearCurrentCompany,
  clearCompanies,
  clearCompanySegments,
  clearCompanyContacts,
  clearCompanyVersions,
  clearCompanyHistory,
  setFilters,
  setPagination,
} = studyYearCompaniesSlice.actions;

export const studyYearCompaniesReducer = studyYearCompaniesSlice.reducer;
